import { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MarkdownEditor } from '@/shared/components/markdownEditor/MarkdownEditor';
import type {
  LabelView,
  TaskRelationViewRelationType,
  TaskView,
  WorkflowView,
} from '@/lib/api/generated/model';
import {
  RelationsSection,
  useAddTaskRelation,
  type RelationEntry,
} from '@/features/task-relations';
import {
  LabelPicker,
  TaskLabelRow,
  useCreateLabel,
  useReplaceTaskLabels,
} from '@/features/labels';
import {
  hasContent,
  useTaskDraftStore,
  type TaskDraft,
} from '../drafts/taskDraftStore';
import { useAttachmentUploads } from '../hooks/useAttachmentUploads';
import { requiredInputRefusal, useCreateTask } from '../hooks/useCreateTask';
import { AttachmentPicker } from './AttachmentPicker';
import { StagedAttachments } from './StagedAttachments';
import { DRAWER_PROSE_CLASS } from '../prose';
import { taskFormSchema, type TaskFormValues } from '../taskForm';

/** How long typing settles before the draft is written (spec 121 plan §4). */
const DRAFT_WRITE_DELAY_MS = 250;

export interface CreateTaskDialogProps {
  /** The draft this dialog shows and writes to (spec 121). */
  draftKey: string;
  workspaceId: string;
  workflow: WorkflowView;
  /** The workspace's label vocabulary, so a task can be born labelled. */
  labelRegistry: readonly LabelView[];
  /** Discard was pressed: the caller takes the draft away and offers Undo. */
  onDiscard: () => void;
}

/**
 * Collects exactly a title and a description (spec FR-018) — nothing else.
 * A workflow declaring a required input refuses up front, before any field is
 * even usable, because this form has nowhere to collect that value yet
 * (FR-025).
 *
 * Built on `ui/dialog`, like the fourteen other dialogs in this app. It used to
 * open its own `fixed inset-0` markup with a full-screen `<button>` behind the
 * panel standing in for click-outside and a hand-written `Escape` handler — and
 * with no focus trap and no scroll lock, so tabbing out of it walked into the
 * board behind. Every one of those is behaviour the Base UI dialog underneath
 * this primitive already provides, which is the same argument `TaskDrawer`
 * records for having moved onto `ui/sheet`.
 *
 * Mounted only while it is open, so `open` is constant and dismissal is
 * reported through `onOpenChange` — the shape `TaskDrawer` uses.
 *
 * A view of a draft (spec 121): everything entered lives in the draft store,
 * so closing — backdrop, Esc, × or Cancel — folds the dialog into a chip
 * instead of throwing the work away, and only Discard does that.
 */
export function CreateTaskDialog(props: CreateTaskDialogProps) {
  const draft = useTaskDraftStore((s) => s.drafts[props.draftKey]);
  // Discarded or cleared while shown: there is nothing left to show.
  if (!draft) return null;
  return <DraftDialog {...props} draft={draft} />;
}

function DraftDialog({
  draftKey,
  workspaceId,
  workflow,
  labelRegistry,
  onDiscard,
  draft,
}: CreateTaskDialogProps & { draft: TaskDraft }) {
  const updateDraft = useTaskDraftStore((s) => s.updateDraft);
  const minimize = useTaskDraftStore((s) => s.minimize);
  const markCreated = useTaskDraftStore((s) => s.markCreated);
  const clearDraft = useTaskDraftStore((s) => s.clearDraft);
  const createTask = useCreateTask();
  const addRelation = useAddTaskRelation(workspaceId);
  const replaceLabels = useReplaceTaskLabels();
  const createLabel = useCreateLabel();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const stagedRelations = draft.relations;
  // Read from the store, not the render: a relation is added from inside an
  // async picker, and two in a row must not overwrite each other.
  const setStagedRelations = (
    next: (prev: RelationEntry[]) => RelationEntry[],
  ) => {
    const current = useTaskDraftStore.getState().drafts[draftKey];
    if (current) updateDraft(draftKey, { relations: next(current.relations) });
  };
  const refusal = requiredInputRefusal(workflow);
  // Files staged before the task exists, uploaded once it does (spec 120). The
  // queue is the draft's, so it keeps going while the dialog is folded away.
  const uploads = useAttachmentUploads(workspaceId, draftKey);
  // Once the task exists, submitting again would create a second one. Read
  // from the draft, so a dialog reopened from its chip still knows.
  const taskCreated = draft.createdTaskId !== null;
  // Relations and labels have all been written; what remains is uploads. A
  // dialog reopened after the create finds them settled.
  const [followUpsDone, setFollowUpsDone] = useState(taskCreated);

  // Labels chosen before the task exists, so they cannot be written until it
  // does. Held in the draft rather than the form: they are not a field the
  // resolver validates, and threading them through `react-hook-form` would buy
  // nothing.
  const labelIds = draft.labelIds;
  const setLabelIds = (ids: string[]) => {
    updateDraft(draftKey, { labelIds: ids });
  };
  const created = draft.createdLabels;
  const vocabulary = [
    ...new Map(
      [...labelRegistry, ...created].map((label) => [label.id, label]),
    ).values(),
  ];
  // The chosen labels as chips, which is also exactly the optimistic set the
  // follow-up write sends — derived once so the two cannot drift.
  const chosen = vocabulary
    .filter((label) => labelIds.includes(label.id))
    .map((label) => ({
      id: label.id,
      name: label.name,
      color: label.color,
      actor_type: 'user' as const,
    }));

  useEffect(() => {
    // Remember where focus came from (the "+" control, or a draft's chip) so
    // dismissing the dialog returns the reader there rather than dropping them
    // at the top of the document.
    const previouslyFocused = document.activeElement;
    return () => {
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, []);

  const {
    register,
    handleSubmit,
    control,
    subscribe,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: { title: draft.title, description: draft.description },
  });

  // The text goes to the draft once typing settles, so storage is not
  // written on every keystroke. `written` is what this dialog last sent: a
  // draft that differs from it was changed elsewhere — another tab — and the
  // field takes that change (FR-011, the last edit wins).
  const written = useRef({
    title: draft.title,
    description: draft.description,
  });
  const pendingWrite = useRef<ReturnType<typeof setTimeout> | null>(null);
  const writeText = () => {
    if (pendingWrite.current !== null) clearTimeout(pendingWrite.current);
    pendingWrite.current = null;
    // A refusing workflow renders no fields, so there is nothing to read —
    // and empty values would overwrite the draft's kept text (FR-013).
    if (refusal) return;
    const { title = '', description = '' } = getValues();
    if (
      title === written.current.title &&
      description === written.current.description
    ) {
      return;
    }
    written.current = { title, description };
    updateDraft(draftKey, { title, description });
  };
  const writeTextRef = useRef(writeText);
  useEffect(() => {
    writeTextRef.current = writeText;
  });
  useEffect(() => {
    const unsubscribe = subscribe({
      formState: { values: true },
      callback: () => {
        if (pendingWrite.current !== null) clearTimeout(pendingWrite.current);
        pendingWrite.current = setTimeout(() => {
          writeTextRef.current();
        }, DRAFT_WRITE_DELAY_MS);
      },
    });
    return () => {
      unsubscribe();
      // Whatever was typed in the last moment is not lost to the delay.
      writeTextRef.current();
    };
  }, [subscribe]);
  useEffect(() => {
    if (draft.title !== written.current.title) {
      setValue('title', draft.title);
    }
    if (draft.description !== written.current.description) {
      setValue('description', draft.description);
    }
    written.current = { title: draft.title, description: draft.description };
  }, [draft.title, draft.description, setValue]);
  // react-hook-form's own `isSubmitting` only tracks the synchronous
  // handler call — `createTask.mutate` fires and returns immediately, so
  // `isSubmitting` flips back to false well before the request settles and
  // a second click would fire a duplicate create. `createTask.isPending`
  // is the mutation's real in-flight state and is what actually gates the
  // button for the whole request lifetime (spec FR-023).
  // Persists one relation per staged entry (research D3/D4: the new task is
  // always `parent_task_id`). Entries that fail stay staged so the person can
  // retry or drop them, rather than silently losing the pick.
  const persistStagedRelations = async (task: TaskView) => {
    const results = await Promise.allSettled(
      stagedRelations.map((entry) =>
        addRelation.mutateAsync({
          parent_task_id: task.id,
          child_task_id: entry.relatedTask.id,
          relation_type:
            entry.relationType === 'plain' ? undefined : entry.relationType,
        }),
      ),
    );
    const failed: RelationEntry[] = [];
    let lastMessage = '';
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        failed.push(stagedRelations[index]);
        lastMessage =
          result.reason instanceof Error ? result.reason.message : lastMessage;
      }
    });
    if (failed.length > 0) {
      setStagedRelations(() => failed);
      setSubmitError(
        `Task created; ${failed.length} relation(s) could not be added: ${lastMessage}`,
      );
      return;
    }
    if (labelIds.length === 0) {
      setFollowUpsDone(true);
      return;
    }
    // Two writes, because a task is created before it can be labelled —
    // there is no field on the create request that carries them. The
    // second is skipped entirely when nothing was chosen, so the ordinary
    // create stays one request.
    replaceLabels.mutate(
      {
        workspaceId,
        taskId: task.id,
        labelIds,
        optimistic: chosen,
      },
      {
        onSuccess: () => {
          setFollowUpsDone(true);
        },
        // The task exists by now, so this is not a failure to create —
        // saying "try again" would invite a duplicate. The dialog stays
        // open, reporting what did and did not happen, and the way out is
        // Close.
        onError: () => {
          setSubmitError(
            'The task was created, but its labels could not be attached. You can add them from the task.',
          );
        },
      },
    );
  };

  const onSubmit = handleSubmit((values) => {
    if (createTask.isPending || replaceLabels.isPending) return;
    setSubmitError(null);
    createTask.mutate(
      { workspaceId, workflow, values },
      {
        onSuccess: (result) => {
          // A step with no linked AI profile is reported as a refusal, not a
          // thrown error (spec 122) — the dialog stays open with the entered
          // values intact and names the step, exactly as a thrown failure
          // already does, so nothing navigates away as if creation succeeded.
          if (result.refusal) {
            setSubmitError(result.refusal.message);
            return;
          }
          const task = result.task;
          markCreated(draftKey, task.id);
          // Attachments start at once, beside relations and labels rather than
          // after them: a relation that fails must not strand the files.
          uploads.startFor(task.id);
          if (stagedRelations.length === 0 && labelIds.length === 0) {
            setFollowUpsDone(true);
            return;
          }
          void persistStagedRelations(task);
        },
        onError: () => {
          setSubmitError(
            'The task could not be created. Your entered values have been kept — try again.',
          );
        },
      },
    );
  });

  // Close once everything after the create has settled and nothing failed:
  // the task exists, so the draft goes with the dialog (FR-006). A failed
  // upload keeps the dialog open with the file's Retry (spec 120 FR-004).
  const uploadsFailed = uploads.failed.length;
  useEffect(() => {
    if (followUpsDone && !uploads.inProgress && uploadsFailed === 0) {
      clearDraft(draftKey);
    }
  }, [clearDraft, draftKey, followUpsDone, uploads.inProgress, uploadsFailed]);

  // Closing never discards (FR-001): the draft folds into a chip, or goes
  // only when there was nothing in it. Uploads keep running either way.
  const requestClose = () => {
    writeText();
    minimize(draftKey);
  };
  const discard = () => {
    writeText();
    onDiscard();
  };
  const [title = '', description = ''] = useWatch({
    control,
    name: ['title', 'description'],
  });
  const showDiscard =
    !taskCreated && hasContent({ ...draft, title, description }, uploads.items);

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) requestClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create task</DialogTitle>
        </DialogHeader>

        {refusal ? (
          <p className="text-muted-foreground text-sm" role="status">
            {refusal.message}
          </p>
        ) : (
          <form
            onSubmit={(event) => {
              void onSubmit(event);
            }}
            className="flex flex-col gap-4"
          >
            {/*
              The fields scroll; the buttons below stay in view. With files
              listed the form can outgrow a short window (spec 120 research R6).
            */}
            <div className="-mx-1 flex max-h-[65vh] flex-col gap-4 overflow-y-auto px-1">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="task-title">Title</Label>
                <Input
                  id="task-title"
                  autoFocus
                  aria-invalid={errors.title ? true : undefined}
                  aria-describedby={
                    errors.title ? 'task-title-error' : undefined
                  }
                  {...register('title')}
                />
                {errors.title ? (
                  <p id="task-title-error" className="text-destructive text-xs">
                    {errors.title.message}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="task-description">Description</Label>
                {/*
                  Markdown with a toolbar and a preview (spec 145). Controlled
                  rather than registered: the editor edits the text itself, and
                  a `Controller` hears those edits as the form's own changes,
                  with the same validation and the same draft write.
                */}
                <Controller
                  control={control}
                  name="description"
                  render={({ field }) => (
                    <MarkdownEditor
                      id="task-description"
                      className="max-h-64"
                      spellCheck={false}
                      aria-invalid={errors.description ? true : undefined}
                      aria-describedby={
                        errors.description
                          ? 'task-description-error'
                          : undefined
                      }
                      name={field.name}
                      ref={field.ref}
                      value={field.value ?? ''}
                      onValueChange={field.onChange}
                      onBlur={field.onBlur}
                      previewClassName={DRAWER_PROSE_CLASS}
                    />
                  )}
                />
                {errors.description ? (
                  <p
                    id="task-description-error"
                    className="text-destructive text-xs"
                  >
                    {errors.description.message}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="task-labels">Labels</Label>
                {/*
                What the task will be born carrying. The popup closes over the
                field, so without this the only record of a choice is inside a
                list that is no longer open — someone picks two labels, closes
                it, and has nothing to check before submitting.
              */}
                {/*
                `TaskLabelRow`, the same row the drawer shows — so a label
                picked by mistake comes off here the way it comes off there.
                These chips used to be `LabelChipRow`'s, which is inert by
                construction, so the only way to drop one was to reopen the
                combobox and find it again in the list. `onNarrow` is omitted
                because there is no board behind this dialog to narrow, which is
                exactly the case that prop is optional for.
              */}
                <TaskLabelRow
                  labels={chosen}
                  onRemove={(labelId) => {
                    setLabelIds(labelIds.filter((id) => id !== labelId));
                  }}
                />
                <LabelPicker
                  id="task-labels"
                  workspaceId={workspaceId}
                  registry={vocabulary}
                  selected={labelIds}
                  onChange={setLabelIds}
                  onCreate={async (name) => {
                    const label = await createLabel.mutateAsync({
                      workspaceId,
                      name,
                    });
                    const current =
                      useTaskDraftStore.getState().drafts[draftKey];
                    updateDraft(draftKey, {
                      createdLabels: [...(current?.createdLabels ?? []), label],
                    });
                    return label;
                  }}
                />
              </div>

              <RelationsSection
                workspaceId={workspaceId}
                relations={stagedRelations}
                onAdd={(
                  task: TaskView,
                  relationType: TaskRelationViewRelationType | undefined,
                ) =>
                  setStagedRelations((prev) => [
                    ...prev,
                    {
                      relationId: null,
                      relatedTask: task,
                      relationType: relationType ?? 'plain',
                      direction: 'child',
                    },
                  ])
                }
                onRemove={(entry) =>
                  setStagedRelations((prev) =>
                    prev.filter(
                      (staged) =>
                        staged.relatedTask.id !== entry.relatedTask.id,
                    ),
                  )
                }
                excludeTaskIds={stagedRelations.map(
                  (entry) => entry.relatedTask.id,
                )}
                isPending={createTask.isPending || addRelation.isPending}
              />

              <div className="flex flex-col gap-1.5">
                <Label>Attachments</Label>
                <AttachmentPicker onFiles={(files) => uploads.add(files)} />
                {/*
                  A page cannot keep a picked file across a reload, so a
                  restored draft says which ones it had (FR-009) rather than
                  quietly arriving without them.
                */}
                {draft.lostFileNames.length > 0 ? (
                  <p className="text-muted-foreground text-xs">
                    These files need to be added again:{' '}
                    {draft.lostFileNames.join(', ')}
                  </p>
                ) : null}
                <StagedAttachments
                  items={uploads.items}
                  onRemove={uploads.remove}
                  onRetry={uploads.retry}
                />
              </div>

              {submitError ? (
                <p role="alert" className="text-destructive text-sm">
                  {submitError}
                </p>
              ) : null}
              {taskCreated && uploadsFailed > 0 && !uploads.inProgress ? (
                <p role="alert" className="text-destructive text-sm">
                  Task created; {uploadsFailed} attachment(s) did not upload.
                </p>
              ) : null}
            </div>

            {/* Stacked and full width on a phone, Create task on top (spec 269). */}
            <div className="flex justify-end gap-2 max-md:flex-col-reverse max-md:[&>button]:w-full">
              {showDiscard ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="mr-auto"
                  onClick={discard}
                >
                  Discard
                </Button>
              ) : null}
              <Button type="button" variant="outline" onClick={requestClose}>
                {taskCreated ? 'Close' : 'Cancel'}
              </Button>
              <Button
                type="submit"
                disabled={
                  createTask.isPending ||
                  addRelation.isPending ||
                  replaceLabels.isPending ||
                  taskCreated
                }
              >
                {createTask.isPending ||
                addRelation.isPending ||
                replaceLabels.isPending
                  ? 'Creating…'
                  : 'Create task'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
